import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';

import { usePagination } from '../../hooks/usePagination';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import { formatDate, formatMontant, formatNombre } from '../../lib/formats';
import { libelle, nomLisible, referenceLisible } from '../../lib/libelles';
import { cn } from '../../lib/utils';
import { BarrePagination } from '../ui/pagination-bar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { LoadingState } from '../ui/loading-state';

/**
 * Onglet « À faire » de l'écran Argent (revue de l'écran Argent, lot 2).
 *
 * Un seul tableau groupé, une ligne d'environ 40 px par dossier, dans l'ordre
 * du geste: facturer, encaisser, confirmer, contrôler, justifier, reprendre.
 * Les groupes vides ne s'affichent pas. « Depuis » est une ancienneté (date
 * d'émission d'une facture, de livraison d'une commande), jamais un retard.
 * À droite, fixe au défilement: soldes des comptes et derniers mouvements.
 */

export const GROUPES_A_FAIRE = [
  { cle: 'facturer', titre: 'À facturer' },
  { cle: 'preuves', titre: 'Preuves à valider' },
  { cle: 'encaisser', titre: 'À encaisser', precision: 'les plus anciennes' },
  { cle: 'confirmer', titre: 'À confirmer' },
  { cle: 'controler', titre: 'À contrôler' },
  { cle: 'justifier', titre: 'Avances à justifier' },
  { cle: 'completer', titre: 'À compléter' },
  { cle: 'reprendre', titre: 'À reprendre' },
];

const JOUR = 24 * 60 * 60 * 1000;

/** « livrée il y a 3 j », « avance aujourd'hui »: une ancienneté, jamais un retard. */
export const anciennete = (date, prefixe, maintenant = Date.now()) => {
  if (!date) return null;
  const moment = new Date(date).getTime();
  if (!Number.isFinite(moment)) return null;
  const jours = Math.max(0, Math.floor((maintenant - moment) / JOUR));
  return `${prefixe ? `${prefixe} ` : ''}${jours === 0 ? 'aujourd’hui' : `il y a ${jours} j`}`;
};

/** « émise il y a 12 j »: l'ancienneté d'une facture, depuis sa date d'émission. */
export const ageFacture = (jours) => {
  const n = Number(jours);
  if (jours == null || !Number.isFinite(n)) return null;
  return n <= 0 ? 'émise aujourd’hui' : `émise il y a ${n} j`;
};

// Dans ces groupes, le geste varie d'une ligne à l'autre: il se lit devant le nom.
const GESTE_VARIABLE = ['confirmer', 'controler', 'completer', 'reprendre'];
const volume = (litres) => (Number(litres) ? `${formatNombre(litres)} L` : null);
const joindre = (...morceaux) => morceaux.filter(Boolean).join(' · ');
const ONGLET_DU_TIERS = {
  CUSTOMER: ['clients', 'client'],
  DRIVER: ['chauffeurs', 'chauffeur'],
  SUPPLIER: ['fournisseurs', 'fournisseur'],
};

/**
 * Les lignes d'« À faire », groupe par groupe, et le compte de chaque groupe.
 * Pure: testable sans écran, et la page en tire le compteur de l'onglet.
 *
 * - `openSummary`: `/finance/summary` sans période (files du serveur, dix
 *   premières lignes chacune, mais le vrai total);
 * - `position`: `/finance/position` (écritures en attente, chauffeurs);
 * - `reprises`: `{ soldesClients, achats }` à reprendre.
 */
export function lignesAFaire({ openSummary, position, reprises, nomClient = (id, nom) => nomLisible(nom) } = {}) {
  const lignes = [];
  const groupes = {};
  const ajouter = (groupe, total, valeur, items) => {
    if (!items.length) return;
    groupes[groupe] = { total: Math.max(Number(total) || 0, items.length), affichees: items.length, valeur };
    lignes.push(...items.map((ligne, rang) => ({ ...ligne, groupe, id: `${groupe}-${ligne.id || rang}` })));
  };
  const pod = openSummary?.proof_of_delivery || {};
  const creances = openSummary?.receivables || {};
  const ecarts = openSummary?.reconciliations || {};

  ajouter('facturer', pod.billing_ready_count, pod.billing_ready_value, (pod.billing_ready_queue || []).map((item) => ({
    id: item.order_id || item.trip_id,
    quoi: 'Créer la facture',
    qui: nomLisible(item.client_name, 'Client à identifier'),
    ref: joindre(item.truck_plate, item.bl_number ? `BL ${item.bl_number}` : null, volume(item.delivered_volume)),
    montant: item.estimated_invoice_value,
    depuis: anciennete(item.delivered_at, 'livrée'),
    route: item.route,
    action: { libelle: 'Créer', type: 'facturer', argent: true, item },
  })));

  ajouter('preuves', pod.pod_review_count, pod.pod_review_value, (pod.pod_review_queue || []).map((item) => ({
    id: item.trip_id,
    quoi: 'Valider la preuve',
    qui: nomLisible(item.client_name, 'Client à identifier'),
    ref: joindre(
      item.truck_plate,
      item.bl_number ? `BL ${item.bl_number}` : null,
      item.status ? libelle('tripStatus', item.status) : null,
      item.ocr_confidence != null ? `lecture ${Math.round(Number(item.ocr_confidence) * 100)} %` : null,
    ),
    montant: item.estimated_invoice_value,
    depuis: anciennete(item.delivered_at, 'livrée'),
    route: item.route,
    action: { libelle: 'Prêt à facturer', type: 'pret', argent: true, item },
  })));

  ajouter('encaisser', creances.open_count, creances.open_value, (creances.open_queue || []).map((item) => ({
    id: item.invoice_id,
    quoi: 'Encaisser',
    qui: nomClient(item.client_id, item.client_name),
    ref: referenceLisible(item.invoice_number, 'Facture sans numéro'),
    montant: item.amount_remaining,
    depuis: ageFacture(item.age_days),
    action: { libelle: 'Encaisser', type: 'encaisser', argent: true, item },
  })));

  const attente = position?.pending || {};
  ajouter('confirmer', attente.count, null, (attente.items || []).map((item) => {
    const [onglet, cle] = ONGLET_DU_TIERS[item.party_type] || ['caisse', 'compte'];
    const tiers = onglet === 'caisse' ? item.account_id : item.party_id;
    return {
      id: item.id,
      quoi: `Confirmer · ${libelle('financeKind', item.kind)}`,
      qui: nomLisible(item.party_name, item.account_name || ''),
      ref: joindre(item.reference, item.party_name ? item.account_name : null),
      montant: item.amount,
      depuis: anciennete(item.occurred_at, ''),
      action: { libelle: 'Ouvrir', type: 'onglet', onglet, extra: { [cle]: tiers } },
    };
  }));

  const nbBloques = Number(ecarts.blocked_reconciliation_count || 0);
  const nbAttente = Number(ecarts.pending_reconciliation_count || 0);
  const nbIncomplets = Number(ecarts.incomplete_reconciliation_count || 0);
  const controles = (pod.cash_blocked_queue || []).map((item) => ({
    id: item.trip_id,
    quoi: 'Contrôler la livraison',
    qui: nomLisible(item.client_name, 'Client à identifier'),
    ref: joindre(
      item.truck_plate,
      item.status ? libelle('tripStatus', item.status) : null,
      item.volume_variance_liters ? `écart ${formatNombre(Math.abs(Number(item.volume_variance_liters)))} L` : null,
      Number(item.estimated_loss_value) ? `perte ${formatMontant(item.estimated_loss_value)}` : null,
    ),
    montant: item.estimated_invoice_value,
    route: item.route || `/mission-integrity?trip_id=${item.trip_id}`,
    action: { libelle: 'Ouvrir', type: 'route', route: item.route || `/mission-integrity?trip_id=${item.trip_id}` },
  }));
  if (nbBloques + nbAttente > 0) {
    controles.push({
      id: 'ecarts',
      quoi: 'Décider des écarts de volume',
      qui: `${nbBloques + nbAttente} écart${nbBloques + nbAttente > 1 ? 's' : ''}`,
      action: { libelle: 'Ouvrir', type: 'route', route: '/stock?tab=variances' },
    });
  }
  if (nbIncomplets > 0) {
    controles.push({
      id: 'volumes',
      quoi: 'Compléter les volumes',
      qui: `${nbIncomplets} trajet${nbIncomplets > 1 ? 's' : ''}`,
      action: { libelle: 'Ouvrir', type: 'route', route: '/trips' },
    });
  }
  ajouter('controler', Number(pod.pod_blocked_count || 0) + controles.filter((l) => !l.route).length, pod.pod_blocked_value, controles);

  const chauffeurs = position?.drivers || {};
  ajouter('justifier', chauffeurs.holding_count, chauffeurs.holding, (chauffeurs.top || []).map((item) => ({
    id: item.id,
    quoi: 'Justifier l’avance',
    qui: nomLisible(item.name, 'Chauffeur'),
    montant: item.balance,
    depuis: anciennete(item.last_advance_at, 'avance'),
    action: { libelle: 'Ouvrir', type: 'onglet', onglet: 'chauffeurs', extra: { chauffeur: item.id } },
  })));

  // Un compte sans solde d'ouverture ne compte pas dans l'argent disponible:
  // il se complète dans « Caisse et banque », fenêtre ouverte d'office.
  const inconnus = (position?.treasury?.accounts || []).filter((compte) => compte.balance == null);
  ajouter('completer', inconnus.length, null, inconnus.map((compte) => ({
    id: compte.id,
    quoi: 'Solde d’ouverture',
    qui: nomLisible(compte.name, 'Compte'),
    ref: libelle('compteTresorerie', compte.account_type),
    action: { libelle: 'Compléter', type: 'onglet', onglet: 'caisse', extra: { compte: compte.id, completer: '1' } },
  })));

  const aReprendre = [];
  if (Number(reprises?.soldesClients) > 0) {
    aReprendre.push({
      id: 'soldes',
      quoi: 'Rapprocher les soldes historiques',
      qui: `${reprises.soldesClients} client${reprises.soldesClients > 1 ? 's' : ''}`,
      action: { libelle: 'Ouvrir', type: 'onglet', onglet: 'clients' },
    });
  }
  if (Number(reprises?.achats) > 0) {
    aReprendre.push({
      id: 'achats',
      quoi: 'Reprendre les achats des stations',
      qui: `${reprises.achats} achat${reprises.achats > 1 ? 's' : ''}`,
      action: { libelle: 'Ouvrir', type: 'onglet', onglet: 'fournisseurs' },
    });
  }
  ajouter('reprendre', aReprendre.length, null, aReprendre);

  const total = Object.values(groupes).reduce((somme, groupe) => somme + groupe.total, 0);
  return { lignes, groupes, total };
}

const EnTeteGroupe = ({ groupe, info, colonnes, onVoirTout }) => (
  <tr className="bg-muted/40" data-testid={`groupe-${groupe.cle}`}>
    <td colSpan={colonnes} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      <span>{groupe.titre}</span>
      {groupe.precision && info.total > info.affichees && <span className="normal-case"> · {groupe.precision}</span>}
      <span className="tabular-nums"> ({info.total > info.affichees ? `${info.affichees} sur ${info.total}` : info.total})</span>
      {Number(info.valeur) > 0 && <span className="tabular-nums normal-case"> · {formatMontant(info.valeur)}</span>}
      {onVoirTout && info.total > info.affichees && (
        <button type="button" className="ml-2 normal-case text-primary hover:underline" onClick={onVoirTout}>Voir tout</button>
      )}
    </td>
  </tr>
);

const ColonneDroite = ({ position, statut, onOuvrirCaisse }) => {
  if (statut === 'error') return null;
  const comptes = position?.treasury?.accounts || [];
  const mouvements = position?.recent_movements || [];
  return (
    <aside className="space-y-3 lg:sticky lg:top-4 lg:self-start" aria-label="Caisse et banque">
      <section className="rounded-xl border bg-card p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Caisse et banque</h3>
        {!position ? (
          <div className="mt-2 space-y-2" aria-hidden="true">
            {[0, 1, 2].map((i) => <div key={i} className="shimmer h-4 rounded bg-muted" />)}
          </div>
        ) : comptes.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">Aucun compte.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {comptes.map((compte) => (
              <li key={compte.id} className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate" title={compte.name}>{nomLisible(compte.name, 'Compte')}</span>
                {compte.balance == null ? (
                  <span className="shrink-0 rounded-full border border-flame/30 bg-flame/10 px-2 text-xs text-flame-ink">À compléter</span>
                ) : (
                  <span className="shrink-0 tabular-nums">{formatMontant(compte.balance, compte.currency)}</span>
                )}
              </li>
            ))}
            <li className="flex items-baseline justify-between gap-3 border-t pt-1 font-semibold">
              <span>Total</span>
              <span className="tabular-nums">{formatMontant(position.treasury.total, position.currency)}</span>
            </li>
          </ul>
        )}
      </section>
      {position && (
        <section className="rounded-xl border bg-card p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Derniers mouvements</h3>
          {mouvements.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Aucun mouvement.</p>
          ) : (
            <ul className="mt-2 space-y-1.5 text-sm" data-testid="derniers-mouvements">
              {mouvements.map((mouvement) => {
                // Le qui d'abord (client, chauffeur, motif de la dépense), la nature en dessous.
                const qui = mouvement.kind === 'TRANSFER'
                  ? `Vers ${nomLisible(mouvement.target_account_name, 'un autre compte')}`
                  : nomLisible(mouvement.party_name, mouvement.reference || libelle('financeKind', mouvement.kind));
                const nature = joindre(libelle('financeKind', mouvement.kind), mouvement.kind === 'TRANSFER' ? null : mouvement.account_name);
                const signe = Number(mouvement.sign);
                return (
                  <li key={mouvement.id} className="grid grid-cols-[2.6rem_minmax(0,1fr)_auto] gap-x-2">
                    <span className="tabular-nums text-muted-foreground">{formatDate(mouvement.occurred_at).slice(0, 5)}</span>
                    <span className="min-w-0">
                      <span className="block truncate" title={qui}>{qui}</span>
                      <span className="block truncate text-xs text-muted-foreground" title={nature}>{nature}</span>
                    </span>
                    <span className={cn('whitespace-nowrap tabular-nums', signe > 0 && 'text-success')}>
                      {signe > 0 ? '+ ' : signe < 0 ? '− ' : ''}{formatMontant(mouvement.amount, mouvement.currency)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <button type="button" className="mt-2 text-xs font-medium text-primary hover:underline" onClick={onOuvrirCaisse}>
            Ouvrir le journal
          </button>
        </section>
      )}
    </aside>
  );
};

export default function AFaire({
  donnees,
  chargement,
  erreur,
  position,
  positionStatut,
  voitArgent = true,
  onAction,
  onOnglet,
}) {
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [searchParams, setSearchParams] = useSearchParams();
  const filtre = searchParams.get('afaire') || '';
  const recherche = searchParams.get('afaire-q') || '';
  const changer = (cle, valeur) => setSearchParams((precedent) => {
    const suivant = new URLSearchParams(precedent);
    if (valeur) suivant.set(cle, valeur);
    else suivant.delete(cle);
    return suivant;
  }, { replace: cle === 'afaire-q' });

  const { lignes, groupes, total } = donnees;
  const visibles = useMemo(() => {
    const texte = recherche.trim().toLocaleLowerCase('fr-FR');
    return lignes.filter((ligne) => (!filtre || ligne.groupe === filtre)
      && (!texte || [ligne.quoi, ligne.qui, ligne.ref].filter(Boolean).join(' ').toLocaleLowerCase('fr-FR').includes(texte)));
  }, [lignes, filtre, recherche]);
  const pagination = usePagination(visibles, { cle: 'afaire', reinitialiser: `${filtre}|${recherche}` });
  const titres = Object.fromEntries(GROUPES_A_FAIRE.map((groupe) => [groupe.cle, groupe]));
  const voirTout = { encaisser: () => onOnglet('clients'), justifier: () => onOnglet('chauffeurs') };

  const liste = (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            aria-label="Rechercher dans À faire"
            placeholder="Rechercher…"
            value={recherche}
            onChange={(event) => changer('afaire-q', event.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
        {total > 0 && (
          <div className="flex gap-1 overflow-x-auto" role="group" aria-label="Filtrer par groupe">
            {[{ cle: '', titre: 'Tout', nombre: total }, ...GROUPES_A_FAIRE.filter((g) => groupes[g.cle]).map((g) => ({ ...g, nombre: groupes[g.cle].total }))].map((choix) => (
              <button
                key={choix.cle || 'tout'}
                type="button"
                aria-pressed={filtre === choix.cle}
                onClick={() => changer('afaire', choix.cle)}
                className={cn(
                  'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
                  filtre === choix.cle ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:bg-muted',
                )}
              >
                {choix.titre}
                <span className="tabular-nums opacity-80">{formatNombre(choix.nombre)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {erreur}
      {chargement && <LoadingState shape="table" rows={6} columns={5} label="Chargement de ce qu’il reste à faire" />}
      {!chargement && !erreur && (
        <div className="overflow-hidden rounded-xl border bg-card">
          <table className="w-full text-sm" data-testid="table-a-faire">
            <thead className="hidden border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground sm:table-header-group">
              <tr>
                <th className="px-3 py-2 font-semibold">Qui ou quoi</th>
                <th className="px-3 py-2 font-semibold">Référence</th>
                <th className="px-3 py-2 text-right font-semibold">Montant</th>
                <th className="px-3 py-2 font-semibold">Depuis</th>
                <th className="px-3 py-2"><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 && (
                <tr className="h-10">
                  <td colSpan={5} className="px-3 py-2 text-muted-foreground">{lignes.length === 0 ? 'Rien à faire.' : 'Aucune ligne ne correspond.'}</td>
                </tr>
              )}
              {pagination.lignes.map((ligne, index) => {
                const precedente = pagination.lignes[index - 1];
                const nouveauGroupe = !precedente || precedente.groupe !== ligne.groupe;
                const bloque = ligne.action.argent && !enLigne;
                return (
                  <React.Fragment key={ligne.id}>
                    {nouveauGroupe && (
                      <EnTeteGroupe groupe={titres[ligne.groupe]} info={groupes[ligne.groupe]} colonnes={5} onVoirTout={voirTout[ligne.groupe]} />
                    )}
                    <tr className="border-b last:border-0 hover:bg-muted/40">
                      {/* Une ligne par dossier (≈ 40 px): le texte trop long est coupé, entier au survol. */}
                      <td className="w-full max-w-0 px-3 py-1.5">
                        <span className="block truncate" title={joindre(GESTE_VARIABLE.includes(ligne.groupe) ? ligne.quoi : null, ligne.qui, ligne.ref)}>
                          {GESTE_VARIABLE.includes(ligne.groupe) && <span className="font-medium">{ligne.quoi}{ligne.qui ? ' · ' : ''}</span>}
                          <span>{ligne.qui}</span>
                          {ligne.ref && <span className="text-muted-foreground sm:hidden"> · {ligne.ref}</span>}
                        </span>
                        <span className="block text-xs text-muted-foreground sm:hidden">
                          {joindre(ligne.montant != null && Number(ligne.montant) !== 0 ? formatMontant(ligne.montant) : null, ligne.depuis)}
                        </span>
                      </td>
                      <td className="hidden max-w-[16rem] truncate whitespace-nowrap px-3 py-1.5 text-muted-foreground sm:table-cell" title={ligne.ref || undefined}>{ligne.ref || ''}</td>
                      <td className="hidden whitespace-nowrap px-3 py-1.5 text-right tabular-nums sm:table-cell">
                        {ligne.montant != null && Number(ligne.montant) !== 0 ? formatMontant(ligne.montant) : ''}
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-1.5 text-muted-foreground sm:table-cell">{ligne.depuis || ''}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 px-2.5 text-xs"
                          disabled={bloque}
                          onClick={() => onAction(ligne)}
                        >
                          {ligne.action.libelle}
                        </Button>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <BarrePagination pagination={pagination} nom="actions" />
    </div>
  );

  if (!voitArgent) return liste;
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
      {liste}
      <ColonneDroite position={position} statut={positionStatut} onOuvrirCaisse={() => onOnglet('caisse')} />
    </div>
  );
}
